import { useEffect, useMemo, useRef } from 'react'
import { useMetroMapDispatch, useMetroMapStore } from './useMetroMapStore'
import { useSchema } from '../components/useSchema'
import { buildSlugIndex } from './slugIndex'
import { load, prependRecent, save } from './routeSelectionPersistence'
import { resolveStationBySlug } from '../interaction/stationResolver'
import { stationSnapshotFromSchema } from './stationSnapshotFromSchema'

/**
 * Presence-only React component (returns `null`) that bridges the
 * `routeSelection` slice of the MetroMap store with the user's browser
 * localStorage, so a returning user's most-recent origin is restored on
 * cold start and an evolving list of recently-touched stations is kept
 * for a future search-suggestions feature.
 *
 * Mount point: must live inside `<SchemaProvider>` →
 * `<MetroMapStoreProvider>` (Task 10 wires this in `AppShell`). Both
 * providers are required — the component reads schema (slug ↔ id) and
 * the store dispatch/subscribe handles.
 *
 * Hydration order (URL > localStorage > empty):
 *   1. The URL sync (Task 9) populates `routeSelection` synchronously
 *      from `?from=&to=` query params if present.
 *   2. This component then hydrates from localStorage ONLY if the
 *      store's `routeSelection` is still empty (both endpoints null) —
 *      URL state always wins, drift between tabs/devices is intentional.
 *   3. If neither URL nor localStorage carries selection, the store
 *      keeps its initial empty state.
 *
 * **Asymmetric hydration (deliberate):** localStorage hydration restores
 * ONLY the From slot, from `lastFromStationSlug`. The To slot is
 * intentionally NOT restored from localStorage — on an organic page
 * revisit the user should be presented with their familiar origin and
 * an empty destination, free to build a new route. URL deeplinks
 * (`?from=&to=`) still hydrate both endpoints (handled by
 * `RouteSelectionUrlSync`); only the localStorage-only path is
 * From-only.
 *
 * Mirror direction: subscribes to the store and writes the canonical
 * shape (`lastFromStationSlug` + `recentStationSlugs`) to localStorage
 * on every mutation:
 *   - `lastFromStationSlug` reflects the current From slug (or `null`
 *     when cleared).
 *   - `recentStationSlugs` is the recent-stations list — both From and
 *     To slugs are prepended (most-recent first), deduplicated by slug,
 *     capped to {@link RECENT_STATIONS_CAP}. Forward-looking: nothing
 *     reads it yet; the future search-suggestions feature will.
 *
 * To clear, dispatch `RouteFromCleared` / `RouteToCleared` explicitly.
 *
 * Returns `null` because it has no DOM presence — this is a pure
 * side-effect component (the React equivalent of a Swift system
 * service or Android `lifecycleScope` job).
 */
export function RouteSelectionLocalStorageSync(): null {
  const store = useMetroMapStore()
  const dispatch = useMetroMapDispatch()
  const schema = useSchema()
  // StrictMode latch — flipped synchronously at the top of the hydrate
  // effect to prevent the second dev-mode invocation from re-running
  // the hydration logic.
  const hydrationStarted = useRef(false)
  // "Hydration is finished, mirror writes are now safe" gate. Flipped
  // AFTER all hydration dispatches have completed so the mirror handler
  // can ignore those dispatches (otherwise we'd write back the exact
  // shape `load()` just returned).
  const hydrated = useRef(false)

  // Slug ↔ id index. Memoised on the schema reference so a remount
  // doesn't rebuild N maps; `useSchema` returns a stable reference once
  // the fetch resolves.
  const slugIndex = useMemo(() => (schema === null ? null : buildSlugIndex(schema)), [schema])

  // Hydrate ONCE on mount when the schema is loaded. The
  // `hydrationStarted.current` latch survives StrictMode's double-
  // invoke (effects fire twice in dev, ref is preserved across re-runs
  // of the same component instance) — without it we would re-dispatch
  // the persisted snapshot on the second effect tick and bypass the
  // "URL already populated" guard if the user navigated to /route?from=
  // between the two ticks.
  useEffect(() => {
    if (hydrationStarted.current || schema === null) return
    hydrationStarted.current = true

    const current = store.getState().routeSelection
    if (current.fromStation !== null || current.toStation !== null) {
      hydrated.current = true
      return
    }

    const persisted = load()
    if (persisted === null || persisted.lastFromStationSlug === null) {
      hydrated.current = true
      return
    }

    // Restore ONLY From — by design. Even if the recent list contains a
    // valid station that would resolve cleanly, the To slot stays empty
    // so the user can build a fresh route on revisit. URL deeplinks
    // (handled by `RouteSelectionUrlSync`) are the only way to hydrate
    // To on cold start.
    const resolved = resolveStationBySlug(schema, persisted.lastFromStationSlug)
    if (resolved !== null) {
      dispatch({
        kind: 'RouteFromSelected',
        station: stationSnapshotFromSchema(resolved),
      })
    }
    // Open the mirror gate AFTER the hydration dispatch has completed.
    // Any subsequent dispatch is a real user mutation worth persisting;
    // hydration's own dispatch must not round-trip back to localStorage.
    hydrated.current = true
  }, [schema, store, dispatch])

  // Mirror store → localStorage on every mutation. Subscribes only — the
  // initial render does NOT write (subscribe handlers fire on dispatch,
  // not on subscribe-time, so no spurious save with a freshly-empty
  // selection on cold mount). Unsubscribes on unmount.
  useEffect(() => {
    if (slugIndex === null) return
    const handler = () => {
      // Suppress writes that originate from hydration itself — otherwise
      // a cold-start with seeded localStorage would persist back the
      // exact shape `load()` just returned (one redundant setItem per
      // mount).
      if (!hydrated.current) return
      const sel = store.getState().routeSelection
      const fromSlug =
        sel.fromStation === null ? null : (slugIndex.slugById.get(sel.fromStation.id) ?? null)
      const toSlug =
        sel.toStation === null ? null : (slugIndex.slugById.get(sel.toStation.id) ?? null)

      // Recent list: prepend whichever slugs are currently non-null,
      // dedup, cap. We seed from the previously-persisted list so a
      // session with multiple mutations builds an actual history rather
      // than overwriting it on each tick. Order matters — prepending
      // From last would put From at the head; we want the more recently
      // touched of the two slots at the head, but since both are
      // observed from the same post-dispatch snapshot we approximate by
      // prepending From first then To (To wins the head when both are
      // present, which matches the typical user flow where To is set
      // after From).
      const previous = load()
      const previousRecent = previous?.recentStationSlugs ?? []
      let recent: readonly string[] = previousRecent
      if (fromSlug !== null) recent = prependRecent(recent, fromSlug)
      if (toSlug !== null) recent = prependRecent(recent, toSlug)

      save({
        lastFromStationSlug: fromSlug,
        recentStationSlugs: recent,
      })
    }
    return store.subscribe(handler)
  }, [slugIndex, store])

  return null
}
